import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchWithAuthRetry, handleApiResponse } from './apiUtils';
import type { ThemeAssetDto } from './themeAssetApi';
import type { AssistantSettings, AssistantTranslations, PublishJobDto } from '@course-authoring/shared-types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Shape returned by GET /api/account-theme and PUT /api/account-theme.
// Fields are the stored values or null when nothing has been saved yet.
export interface AccountThemeDto {
  themeColors: {
    sidebar?: string | null;
    sidebarLogo?: string | null;
    accent?: string | null;
  } | null;
  lessonColors: {
    color1?: string | null;
    color2?: string | null;
    color3?: string | null;
    color4?: string | null;
  } | null;
  fontFamily: string | null;
  primaryLogoAssetId: number | null;
  certificateLogoAssetId: number | null;
  customFontAssetId: number | null;
  primaryLogo: ThemeAssetDto | null;
  certificateLogo: ThemeAssetDto | null;
  customFont: ThemeAssetDto | null;
  assistant?: AssistantSettings | null;
  assistantAvatarAssetId?: number | null;
  assistantAvatar?: ThemeAssetDto | null;
  assistantTranslations?: AssistantTranslations | null;
  themeId?: number | null;
  themeExternalAccountId?: number | null;
  isInherited?: boolean;
  publishJob?: PublishJobDto | null;
}

export interface SaveAccountThemeDto extends AccountThemeDto {
  admittedPublishJob: Pick<PublishJobDto, 'id' | 'attempt'> | null;
}

export interface SaveAccountThemePayload {
  externalAccountId: number | null;
  themeColors: { sidebar: string; sidebarLogo: string; accent: string };
  lessonColors: { color1: string; color2: string; color3: string; color4: string };
  fontFamily: string;
  primaryLogoAssetId: number | null;
  certificateLogoAssetId: number | null;
  customFontAssetId: number | null;
  assistant?: AssistantSettings;
  assistantAvatarAssetId?: number | null;
}

export class AccountThemeSaveError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly publishJob?: PublishJobDto | null,
  ) {
    super(message);
    this.name = 'AccountThemeSaveError';
  }
}

export const accountThemeKeys = {
  all: ['account-theme'] as const,
  scope: (accountId: number | null) =>
    [...accountThemeKeys.all, 'scope', accountId ?? 'base'] as const,
};

export async function fetchAccountTheme(
  accountId: number | null,
  signal?: AbortSignal,
): Promise<AccountThemeDto> {
  const params = new URLSearchParams();
  params.set('externalAccountId', accountId === null ? 'null' : String(accountId));

  const response = await fetchWithAuthRetry(
    `${API_BASE_URL}/account-theme?${params.toString()}`,
    { signal },
    { json: 'withBody' },
  );
  return handleApiResponse<AccountThemeDto>(response, 'Failed to load account theme');
}

export async function saveAccountTheme(payload: SaveAccountThemePayload): Promise<SaveAccountThemeDto> {
  const response = await fetchWithAuthRetry(
    `${API_BASE_URL}/account-theme`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null) as {
      message?: string;
      code?: string;
      publishJob?: PublishJobDto | null;
    } | null;
    throw new AccountThemeSaveError(
      body?.message ?? response.statusText ?? 'Failed to save account theme',
      response.status,
      body?.code,
      body?.publishJob,
    );
  }
  return response.json() as Promise<SaveAccountThemeDto>;
}

export function useAccountTheme(
  accountId: number | null,
  options?: { enabled?: boolean; refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: accountThemeKeys.scope(accountId),
    queryFn: ({ signal }) => fetchAccountTheme(accountId, signal),
    enabled: options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
  });
}

export function useSaveAccountTheme() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveAccountTheme,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(accountThemeKeys.scope(variables.externalAccountId), data);
      queryClient.invalidateQueries({ queryKey: accountThemeKeys.all });
    },
  });
}
